import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import { getCapabilities, Capabilities } from "../api/authAPI";
import { getAuthToken } from "../api/apiHelpers";

export interface PermissionContextType {
  permissions: Set<string>;
  isElevated: boolean;
  loading: boolean;
  hasPermission: (name: string) => boolean;
  refresh: () => Promise<void>;
  /** Full server-derived authz summary (null until loaded / when logged out). */
  capabilities: Capabilities | null;
}

const defaultContext: PermissionContextType = {
  permissions: new Set(),
  isElevated: false,
  loading: true,
  hasPermission: () => false,
  refresh: async () => {},
  capabilities: null,
};

export const PermissionContext = createContext<PermissionContextType>(defaultContext);

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Loads the current user's server-derived capabilities from
 * /api/auth/capabilities once on mount: role flags (is_staff, is_member,
 * can_enroll_any_program) plus the explicit permission grants. Elevated
 * roles (admin / tenant_owner / super_admin / platform_admin / central
 * source) bypass — hasPermission always returns true for them, matching
 * the backend's requirePermission middleware behavior.
 *
 * Prefer reading role semantics from `capabilities` over localStorage
 * userRole strings — the server is the single source of truth, and the
 * native app reads the identical object (mobile backend-first effort).
 *
 * The provider is a no-op when no auth token is present (public/landing
 * pages) — in that state hasPermission returns false for everything.
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setCapabilities(null);
      setLoading(false);
      return;
    }
    try {
      const caps = await getCapabilities();
      setCapabilities(caps);
    } catch {
      // Network error or 401 — treat as no perms. The actual gate is
      // backend-enforced anyway; the hook is a UX optimization.
      setCapabilities(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isElevated = !!capabilities?.is_elevated;
  const permissions = React.useMemo(
    () => new Set(capabilities?.permissions || []),
    [capabilities]
  );

  const hasPermission = useCallback(
    (name: string) => {
      if (isElevated) return true;
      return permissions.has(name);
    },
    [isElevated, permissions]
  );

  return (
    <PermissionContext.Provider
      value={{ permissions, isElevated, loading, hasPermission, refresh, capabilities }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
